import React, { useState, useEffect, useCallback } from 'react';
import { FormField } from '../common/form-field.component';
import { LogoUpload } from '../common/logo-upload.component';
import { InvoicePreview } from './invoice-preview.component';
import { OTPModal } from '../common/otp-modal.component';
import { ConfirmationModal } from '../common/confirmation-modal.component';
import { useInvoice } from '../../hooks/useInvoice';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganization } from '../../features/organization/organization.slice';
import { RootState, AppDispatch } from '../../store';
import './invoice-config.style.scss';
import { notify } from '../../helper/notify';
import axios from 'axios';
import { getItem } from '../../helper/localstorage.helper';
import { updateUserOnboardingService } from '../../features/userOnboarding/userOnboarding.slice';
import { getUserOnboarding } from '../../../redux/auth/authSlice';
import { useTranslation } from 'react-i18next';
import apiCall from '../../../api';
import { isGuestInvozioEmail } from '../../helper/common.helper';
import { BankMethodSelector } from '../bank/bank-method-selector.component';
import { BankMethodValue } from '../../features/bank/bank.types';
import { deriveBankMethodValue, buildManualBankPayload, isValidClearingNumber, isValidAccountNumber, hasCompleteBankInfo } from '../../features/bank/bank.helpers';
import { hasPendingBriteReturn } from '../../features/bank/brite-resume';
import { getCustomerDob } from '../../utils/registration-validation';

export interface InvoiceConfigData {
  organizationName: string;
  logo: File | null;
  bankName: string;
  accountNumber: string;
  clearingNumber: string;
  ibanNumber?: string;
  bicNumber?: string;
  otherBankName?: string;
}

export interface InvoiceConfigProps {
  initialData?: Partial<InvoiceConfigData>;
  onChange?: (data: InvoiceConfigData) => void;
  onSubmit?: (data: InvoiceConfigData) => void;
  onSkip?: () => void;
  showHeader?: boolean;
  showPreview?: boolean;
  submitButtonText?: string;
  className?: string;
  validationRules?: Partial<Record<keyof InvoiceConfigData, (value: any) => string | undefined>>;
  readOnly?: boolean;
}

export const InvoiceConfig: React.FC<InvoiceConfigProps> = ({
  initialData = {},
  onChange,
  onSubmit,
  showHeader = true,
  showPreview = false,
  submitButtonText = "SAVE_AND_PROCEED",
  className = '',
  validationRules = {},
  readOnly = false
}) => {
  const {
    bankDetails,
    loading,
    error,
    message,
    otpLoading,
    saveLoading,
    clearError,
    clearMessage,
    generateOTP,
    saveBankDetails,
    loadBankDetails
  } = useInvoice();

  const dispatch: AppDispatch = useDispatch();
  const {
          personalData
      } = useSelector((state: any) => state.profileSettings);
  const user = useSelector((state: any) => state.auth.user);
const { data: organizationData, loading: orgLoading, error: orgError } = useSelector((state: RootState) => state.organization);
const [formData, setFormData] = useState<InvoiceConfigData>({
    organizationName: initialData?.signUpUser?.organizationName || '',
    logo: initialData.logo || null,
    bankName: initialData.bankName || '',
    accountNumber: initialData.accountNumber || '',
    clearingNumber: initialData.clearingNumber || '',
    ibanNumber: initialData.ibanNumber || '',
    bicNumber: initialData.bicNumber || '',
    otherBankName: initialData.otherBankName || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof InvoiceConfigData, string>>>({});
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCounter, setOtpCounter] = useState(300);
  const [otpResetTrigger, setOtpResetTrigger] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<InvoiceConfigData | null>(null);
  const [bankValue, setBankValue] = useState<BankMethodValue>(() => deriveBankMethodValue({
    bank: initialData.bankName || '',
    clearingNumber: initialData.clearingNumber,
    accountNumber: initialData.accountNumber,
    ibanNumber: initialData.ibanNumber,
    bicNumber: initialData.bicNumber,
  }));

  useEffect(() => {
    if (personalData && !bankValue.bank) {
      setBankValue(deriveBankMethodValue(personalData));
    }
  }, [personalData, bankValue.bank]);
  const [wasResuming] = useState(() => hasPendingBriteReturn('invoice-config'));
  const hasExistingBankInfo = hasCompleteBankInfo(personalData) && !wasResuming;

  // organizationName still comes from the /Settings-sourced record, once it loads.
  useEffect(() => {
    if (bankDetails && bankDetails.length > 0) {
      const firstBank = bankDetails[0];
      if (firstBank?.signUpUser?.organizationName) {
        setFormData(prev => ({
          ...prev,
          organizationName: prev.organizationName || firstBank.signUpUser!.organizationName,
        }));
      }
    }
  }, [bankDetails]);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        logo: initialData.logo || prev.logo,
      }));
      if (initialData.bankName) {
        setBankValue(deriveBankMethodValue({
          bank: initialData.bankName,
          clearingNumber: initialData.clearingNumber,
          accountNumber: initialData.accountNumber,
          ibanNumber: initialData.ibanNumber,
          bicNumber: initialData.bicNumber,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const uploadLogoToAPI = async (logoFile: File): Promise<boolean> => {
    if (!logoFile) return true;

    try {
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = localStorage.getItem('token');

      const formDataLogo = new FormData();
      formDataLogo.append('file', logoFile);

      const response = await apiCall.post(
        `${baseUrl}/signup/invoice-logo`,
        formDataLogo,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        return true;
      } else {
        console.error('Logo upload failed:', response);
        notify.error(((response as any)?.data?.message || t('FAILED_TO_UPLOAD_LOGO')) as string);
        return false;
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || (t('FAILED_TO_UPLOAD_LOGO') as string);
      console.error('Logo upload error:', error);
      notify.error(errorMessage);
      return false;
    }
  };

  const handleGenerateOTP = async () => {
    try {
      const language = getItem("i18nextLng") || 'en';
      await generateOTP(language);
      setShowOTPModal(true);
      setOtpCounter(300);
      setOtpResetTrigger(!otpResetTrigger); // Trigger reset
    } catch (error) {
      console.error('Failed to generate OTP:', error);
    }
  };

  // Shared by the manual-path OTP flow and the Brite path: saves org name +
  // logo and marks the onboarding step complete. Bank details themselves are
  // saved separately (saveBankDetails for manual paths; the backend persists
  // Brite-linked accounts itself via its webhook — see useBrite).
  const finalizeOnboardingStep = async (logo: File | null) => {
    if (logo) {
      await uploadLogoToAPI(logo);
    }
    await dispatch(updateUserOnboardingService({ actionType: 'IsSkipForNow', value: true })).unwrap();
    await dispatch(getUserOnboarding()).unwrap();
    const orgResult = await dispatch(fetchOrganization(formData.organizationName)).unwrap();
    if ((orgResult as any)?.statusCode === 200 || (orgResult as any)?.statusCode === 201) {
      notify.success((orgResult as any)?.message || (t('ORGANIZATION_UPDATED_SUCCESSFULLY') as string));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOTPSubmit = async (otp: string) => {
    if (!pendingFormData) return;

    try {
      const selectedBank = bankDetails?.find(bank => bank.bank === bankValue.bank);
      const payload = {
        id: selectedBank?.id,
        ...buildManualBankPayload(bankValue),
        incomeTax: selectedBank?.incomeTax || 0,
        code: otp,
      };

      const result = await saveBankDetails(payload);
      if (result.payload?.statusCode === 200) {
        await finalizeOnboardingStep(pendingFormData.logo);
      }
      setShowOTPModal(false);
      setPendingFormData(null);
      setOtpResetTrigger(!otpResetTrigger);
      loadBankDetails();

      onSubmit?.(pendingFormData);
    } catch (error) {
      console.error('Failed to save bank details:', error);
      setOtpResetTrigger(!otpResetTrigger);
    }
  };

  const handleOTPResend = () => {
    setOtpCounter(300);
    setOtpResetTrigger(!otpResetTrigger);
    handleGenerateOTP();
  };

  const handleBriteCompleted = async () => {
    try {
      await finalizeOnboardingStep(formData.logo);
      loadBankDetails();
      onSubmit?.(formData);
    } catch (error) {
      console.error('Failed to finalize onboarding after Brite completion:', error);
    }
  };

  const handleInputChange = (field: keyof InvoiceConfigData, value: string | File | null) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    setFormData(newFormData);
    onChange?.(newFormData);

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleLogoSelect = (file: File | null) => {
    if (file) {
      // Same validation as the create-invoice logo upload: JPG/PNG only, ≤ 1MB.
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          logo: t('INVALID_LOGO_FORMAT') as string
        }));
        return;
      }

      if (file.size > 1 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          logo: t('LOGO_FILE_SIZE_EXCEEDED') as string
        }));
        return;
      }
    }

    handleInputChange('logo', file);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InvoiceConfigData, string>> = {};
    Object.entries(validationRules).forEach(([field, rule]) => {
      const value = formData[field as keyof InvoiceConfigData];
      const error = rule?.(value as any);
      if (error) {
        newErrors[field as keyof InvoiceConfigData] = error;
      }
    });

    if (bankValue.method === 'other-swedish') {
      if (!bankValue.otherBankName?.trim()) {
        newErrors.otherBankName = t('BANK_NAME_REQUIRED') as string;
      }
      if (!isValidClearingNumber(bankValue.clearingNumber)) {
        newErrors.clearingNumber = t('CLEARING_NUMBER_INVALID') as string;
      }
      if (!isValidAccountNumber(bankValue.accountNumber)) {
        newErrors.accountNumber = t('ACCOUNT_NUMBER_INVALID') as string;
      }
    } else if (bankValue.method === 'foreign' || bankValue.method === 'revolut') {
      if (!bankValue.ibanNumber?.trim()) {
        newErrors.ibanNumber = 'IBAN number is required';
      }
      if (!bankValue.bicNumber?.trim()) {
        newErrors.bicNumber = 'BIC number is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if(isGuestInvozioEmail(personalData.email)){
        notify.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
        return
    }
    if (validateForm()) {
      setPendingFormData(formData);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    handleGenerateOTP();
  };

  if (hasExistingBankInfo) {
    return null;
  }

  return (
    <div className={`invoice-config ${className}`}>
      {/* {loading && (
        <div className="invoice-config__loading">
          <div className="loading-spinner"></div>
          <span>{t('LOADING_BANK_DETAILS')}</span>
        </div>
      )} */}

      <div className={`invoice-config__content invoice-config__content--with-preview`}>
        <div className="invoice-config__form">
          {/* Brand Name Field */}
          <FormField
            label={t("ORGANIZATION_NAME_OPTIONAL")}
            tooltipContent={t("ORGANIZATION_NAME_TOOLTIP")}
            error={errors.organizationName}
          >
            <input
              type="text"
              className={`form-input ${errors.organizationName ? 'form-input--error' : ''}`}
              placeholder={t("ORGANIZATION_NAME_PLACEHOLDER")}
              value={formData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              disabled={readOnly}
            />
          </FormField>
          <div id="logo-upload-section">
            {/* Logo Upload Field */}
            <FormField
              label={t("UPLOAD_YOUR_LOGO")}
              tooltipContent={t("UPLOAD_YOUR_LOGO_TOOLTIP")}
              error={errors.logo}
            >
              <LogoUpload
                onLogoSelect={handleLogoSelect}
                selectedLogo={formData.logo}
                disabled={readOnly}
                maxSize={1}
                acceptedFormats={['png', 'jpg', 'jpeg']}
              />
            </FormField>
          </div>

          <BankMethodSelector
            value={bankValue}
            onChange={setBankValue}
            errors={{
              clearingNumber: errors.clearingNumber,
              accountNumber: errors.accountNumber,
              ibanNumber: errors.ibanNumber,
              bicNumber: errors.bicNumber,
              otherBankName: errors.otherBankName,
            }}
            disabled={readOnly}
            showRevolutMarketing={bankValue.group === 'international'}
            briteContext={{
              email: personalData?.email || '',
              firstName: personalData?.firstName || '',
              lastName: personalData?.lastName || '',
              dob: personalData?.dateOfBirth === "" ? getCustomerDob(personalData?.ssn) : getCustomerDob(personalData?.dateOfBirth),
              customerAddress: {
                city: personalData?.city || '',
                street: personalData?.address || '',
                postal_code: personalData?.zipCode || '',
                country_id: 'se',
              },
            }}
            onBriteCompleted={handleBriteCompleted}
            onBriteError={(message) => notify.error(message)}
            briteSurface="invoice-config"
          />

          {/* Disclaimer */}
          {bankValue.method !== 'brite' && (
            <div className="invoice-config__disclaimer">
              <svg className="invoice-config__warning-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10 7v6M10 5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <p>
                {t("BANK_DETAILS_DISCLAIMER_TEXT")}
              </p>
            </div>
          )}
          {bankValue.method !== 'brite' && (
            <div className="invoice-config__actions">
              <button
                className="invoice-config__submit-button mb-4"
                onClick={handleSubmit}
                type="button"
              >
                {t(submitButtonText)}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.333 8h9.334M9.333 4.667L12.667 8l-3.334 3.333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {showPreview && (
          <InvoicePreview
            className="invoice-config__preview"
            width='500px'
            brandName={formData.organizationName || "YOUR LOGO"}
            logoUrl={formData.logo ? URL.createObjectURL(formData.logo) : undefined}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        title={t("BANK_INFORMATION") as string}
        message={t('CONFIRM_BANK_DETAILS_MESSAGE') as string}
        confirmText={t("CONFIRM_AND_CONTINUE") as string}
        cancelText={t("NO") as string}
        loading={otpLoading}
      />

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setOtpResetTrigger(!otpResetTrigger);
        }}
        onSubmit={handleOTPSubmit}
        onResend={handleOTPResend}
        loading={saveLoading}
        counter={otpCounter}
        setCounter={setOtpCounter}
        resetTrigger={otpResetTrigger}
      />
    </div>
  );
};
